import React, { useState } from "react";
import Link from "components/link";
import { ReactComponent as GoldArrow } from "svgs/gold-arrow.svg";
import { useSettings } from "hooks/useStore";
import { trackNavClick } from "analytics";
import SubCategoryItem from "./sub-category-item";
import clsx from "clsx/lite";

const CategoryLink = ({ item, isActive, onClick, handleHover }) => (
	<Link
		to={item.url}
		className={clsx("menu-category hover:font-semibold text-sm cursor-pointer",
			isActive && "font-semibold", item.url && "hover:underline")}
		onClick={() => item.url && onClick()}
		onMouseOver={handleHover}
		onFocus={handleHover}
		role="menuitem"
	>
		{item.title}
		{isActive && <GoldArrow />}
	</Link>
);

const SeparatedLink = ({ link, activeCategoryKey, handleClick, handleHover }) => {
	if (link._type === "menuCategory") {
		return (
			<div key={link._key}>
				<Link
					to={link.url}
					className="menu-category text-base hover:opacity-100 min-w-24 font-bold whitespace-normal"
					role="menuitem"
				>
					{link.title}
				</Link>
				{link.categoryLinks?.map((item, index) => (
					<CategoryLink
						key={item._key}
						item={item}
						isActive={item._key === activeCategoryKey}
						onClick={() => handleClick(item)}
						handleHover={() => handleHover(link, index, item)}
					/>
				))}
			</div>
		);
	}

	if (link._type === "link") {
		return (
			<Link
				to={link.url}
				className="menu-category underline text-sm"
				key={link._key}
				onClick={() => handleClick(link)}
			>
				{link.title}
			</Link>
		);
	}
	return null;
};

const MenuCategory = ({ menuItem, activeCategoryKey, handleClick, handleHover }) => (
	<div className="flex flex-col border-r border-gray pr-4 mr-4 xl:mr-10 w-48" key={menuItem._key}>
		<Link
			to={menuItem.url}
			onClick={() => menuItem.url && handleClick(menuItem)}
			className={clsx("menu-category text-base hover:opacity-100 font-bold", menuItem.url && "hover:underline")}
			role="menuitem"
		>
			{menuItem.title}
		</Link>
		{menuItem.categoryLinks?.map((item, index) => (
			<CategoryLink
				key={item._key}
				item={item}
				isActive={item._key === activeCategoryKey}
				onClick={() => handleClick(item)}
				handleHover={() => handleHover(menuItem, index, item)}
			/>
		))}
		{menuItem.separatedLinks?.length > 0 && (
			<div className="pt-3">
				{menuItem.separatedLinks.map(link => (
					<SeparatedLink
						key={link._key}
						link={link}
						activeCategoryKey={activeCategoryKey}
						menuItem={menuItem}
						handleClick={handleClick}
						handleHover={handleHover}
					/>
				))}
			</div>
		)}
	</div>
);

const HorizontalCategoryMenu = ({ data }) => {
	const [activeMenu, setActiveMenu] = useState(() => data?.find(item => item._type === "menuCategory"));
	const [activeIndex, setActiveIndex] = useState(() =>
		activeMenu?.categoryLinks?.findIndex(item => item._type === "menuCategory"));
	const { toggleMenu } = useSettings(["toggleMenu"]);

	const activeCategoryKey = activeMenu?.categoryLinks?.[activeIndex]?._key;

	const handleActiveMenu = (menu, index, item) => {
		if (item.categoryLinks?.some(i => i._type === "productLink")) {
			setActiveMenu(menu);
			setActiveIndex(index);
		}
	};

	const handleOnLinkClick = (item) => {
		toggleMenu(false);
		trackNavClick(item.title);
	};

	return (
		<div className="py-4 px-6 flex flex-0 items-stretch mx-auto w-full standard-section-width">
			<div className="flex mx-auto xl:max-w-[calc(100%-236px)] xl:pl-3 xxl:pl-12">
				<div className="flex flex-row">
					{data?.map((menuItem, index) => {
						if (!menuItem || (menuItem._type !== "link" && menuItem._type !== "menuCategory")) {
							return null;
						}

						if (menuItem._type === "link") {
							return (
								<Link
									to={menuItem.url}
									key={`link_${index}`}
									onClick={() => handleOnLinkClick(menuItem)}
									className="menu-category hover:underline"
								>
									{menuItem.title}
								</Link>
							);
						}

						if (menuItem._type === "menuCategory") {
							return (
								<MenuCategory
									key={`category_${index}`}
									menuItem={menuItem}
									activeCategoryKey={activeCategoryKey}
									handleClick={handleOnLinkClick}
									handleHover={handleActiveMenu}
								/>
							);
						}
						return null;
					})}
				</div>
				{activeMenu?._type !== "link" && (
					<SubCategoryItem
						data={activeMenu}
						activeIndex={activeIndex}
						className="!gap-10"
						itemClassName="!w-1/2 min-w-40"
						limitItems={2}
					/>
				)}
			</div>
		</div>
	);
};

export default HorizontalCategoryMenu;
